export const debounce = <T extends (...args: any[]) => ReturnType<T>>(
  callback: T,
  timeout: number = 1500
): ((...args: Parameters<T>) => void) => {
  let timer: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      callback(...args);
    }, timeout);
  };
};

export const promiseDebounce = <T extends (...args: any[]) => Promise<any>>(
  callback: T,
  timeout: number = 1500
): ((...args: Parameters<T>) => Promise<ReturnType<T> | void>) => {
  let timer: ReturnType<typeof setTimeout>;
  let currentRequest: AbortController | null = null;

  return (...args: Parameters<T>) => {
    if (timer) {
      clearTimeout(timer);
    }
    if (currentRequest) {
      currentRequest.abort();
    }
    const abortController = new AbortController();
    currentRequest = abortController;

    return new Promise((resolve, reject) => {
      timer = setTimeout(async () => {
        try {
          const result = await callback(...args, { signal: abortController.signal });
          if (currentRequest === abortController) {
            currentRequest = null;
            resolve(result);
          }
        } catch (error) {
          if (error instanceof DOMException && error.name === "AbortError") {
            resolve(undefined);
          } else {
            reject(error);
          }
        }
      }, timeout);
    });
  };
};
